import { useMemo, useState } from 'react';
import {
    FiAlertCircle,
    FiCalendar,
    FiCheck,
    FiCreditCard,
    FiDollarSign,
    FiEdit2,
    FiEye,
    FiEyeOff,
    FiPackage,
    FiPause,
    FiPlay,
    FiRefreshCw,
    FiSearch,
    FiSettings,
    FiShield,
    FiTrash2,
    FiUserPlus,
    FiUsers,
} from 'react-icons/fi';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/layout/Sidebar';
import { Button } from '../../components/ui/Button';
import { SearchableSelect, confirmDialog, toast } from '../../components/ui';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { mockUsers as initialMockUsers, mockPlans } from '../../mocks/data';
import type { Plan, PlanFeature, User, UserRole } from '../../types';
import { usePreferences } from '../../contexts';
import styles from './Admin.module.css';

type AdminTab = 'plans' | 'users' | 'settings';
type UserModalMode = 'add' | 'edit' | 'renew' | 'changePlan';

const ALL_FEATURES: { key: PlanFeature; label: string }[] = [
    { key: 'graficos', label: 'Gráficos' },
    { key: 'cashflow', label: 'Fluxo de Caixa' },
    { key: 'reports', label: 'Relatórios' },
    { key: 'settings', label: 'Configurações' },
    // { key: 'export_pdf', label: 'Exportar PDF' },         // sem equivalência
    // { key: 'export_excel', label: 'Exportar Excel' },     // sem equivalência
    // { key: 'custom_categories', label: 'Categorias Personalizadas' }, // sem equivalência
    // { key: 'api_access', label: 'Acesso à API' },         // sem equivalência
    // { key: 'priority_support', label: 'Suporte Prioritário' },    // sem equivalência
];

export const Admin = () => {
    const [activeTab, setActiveTab] = useState<AdminTab>('plans');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    // User management state
    const [users, setUsers] = useState<User[]>(initialMockUsers);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [userModalMode, setUserModalMode] = useState<UserModalMode>('add');
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [userFormData, setUserFormData] = useState({
        name: '',
        email: '',
        role: 'user' as UserRole,
        planId: 'free',
        status: 'active' as User['status'],
        planExpiresAt: '',
    });

    // Form state for plan editing
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: 0,
        maxAccounts: 5,
        maxTransactionsPerMonth: 100,
        features: [] as PlanFeature[],
    });

    const { formatCurrency } = usePreferences();

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    // Stats
    const totalUsers = users.length;
    const totalPlans = mockPlans.filter(p => p.isActive).length;
    const estimatedRevenue = useMemo(() => {
        return users.reduce((acc, user) => {
            const plan = mockPlans.find(p => p.id === user.planId);
            return acc + (plan?.price || 0);
        }, 0);
    }, [users]);
    const activeSubscriptions = users.filter(u => u.planId && u.planId !== 'free' && u.status === 'active').length;
    const activeUsers = users.filter(u => u.status === 'active').length;
    const inactiveUsers = users.filter(u => u.status !== 'active').length;

    const filteredUsers = useMemo(() => {
        return users.filter(
            user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, users]);

    const handleEditPlan = (plan: Plan) => {
        setEditingPlan(plan);
        setFormData({
            name: plan.name,
            description: plan.description,
            price: plan.price,
            maxAccounts: plan.maxAccounts,
            maxTransactionsPerMonth: plan.maxTransactionsPerMonth,
            features: [...plan.features],
        });
        setIsModalOpen(true);
    };

    const handleNewPlan = () => {
        setEditingPlan(null);
        setFormData({
            name: '',
            description: '',
            price: 0,
            maxAccounts: 5,
            maxTransactionsPerMonth: 100,
            features: ['graficos', 'settings'],
        });
        setIsModalOpen(true);
    };

    const handleFeatureToggle = (feature: PlanFeature) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.includes(feature)
                ? prev.features.filter(f => f !== feature)
                : [...prev.features, feature],
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Aqui você salvaria o plano
        console.log('Saving plan:', formData);
        setIsModalOpen(false);
    };

    const getPlanName = (planId?: string) => {
        const plan = mockPlans.find(p => p.id === planId);
        return plan?.name || 'Sem plano';
    };

    // User management functions
    const handleAddUser = () => {
        setUserModalMode('add');
        setEditingUser(null);
        const nextYear = new Date();
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        setUserFormData({
            name: '',
            email: '',
            role: 'OPERADOR',
            planId: 'free',
            status: 'active',
            planExpiresAt: nextYear.toISOString().split('T')[0],
        });
        setIsUserModalOpen(true);
    };

    const handleEditUser = (user: User) => {
        setUserModalMode('edit');
        setEditingUser(user);
        setUserFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            planId: user.planId || 'free',
            status: user.status,
            planExpiresAt: user.planExpiresAt ? user.planExpiresAt.split('T')[0] : '',
        });
        setIsUserModalOpen(true);
    };

    const handleChangePlan = (user: User) => {
        setUserModalMode('changePlan');
        setEditingUser(user);
        const nextYear = new Date();
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        setUserFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            planId: user.planId || 'free',
            status: user.status,
            planExpiresAt: user.planExpiresAt ? user.planExpiresAt.split('T')[0] : nextYear.toISOString().split('T')[0],
        });
        setIsUserModalOpen(true);
    };

    const handleRenewPlan = (user: User) => {
        setUserModalMode('renew');
        setEditingUser(user);
        const currentExpiry = user.planExpiresAt ? new Date(user.planExpiresAt) : new Date();
        currentExpiry.setFullYear(currentExpiry.getFullYear() + 1);
        setUserFormData({
            name: user.name,
            email: user.email,
            role: user.role,
            planId: user.planId || 'free',
            status: user.status,
            planExpiresAt: currentExpiry.toISOString().split('T')[0],
        });
        setIsUserModalOpen(true);
    };

    const handleToggleUserStatus = (user: User) => {
        setUsers(prev =>
            prev.map(u =>
                u.id === user.id
                    ? { ...u, status: u.status === 'active' ? 'inactive' : 'active' }
                    : u
            )
        );
    };

    const handleSuspendUser = (user: User) => {
        setUsers(prev =>
            prev.map(u =>
                u.id === user.id
                    ? { ...u, status: 'suspended' }
                    : u
            )
        );
    };

    const handleDeleteUser = async (user: User) => {
        if (user.role === 'ADMIN') {
            toast('Não é possível excluir um administrador.', 'error');
            return;
        }
        const confirmed = await confirmDialog(`Tem certeza que deseja excluir o usuário ${user.name}?`);
        if (confirmed) {
            setUsers(prev => prev.filter(u => u.id !== user.id));
        }
    };

    const handleUserSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (userModalMode === 'add') {
            const newUser: User = {
                id: `user-${Date.now()}`,
                name: userFormData.name,
                email: userFormData.email,
                role: userFormData.role,
                planId: userFormData.planId,
                status: userFormData.status,
                planExpiresAt: userFormData.planId !== 'free' ? `${userFormData.planExpiresAt}T00:00:00` : undefined,
                createdAt: new Date().toISOString(),
            };
            setUsers(prev => [...prev, newUser]);
        } else if (editingUser) {
            setUsers(prev =>
                prev.map(u =>
                    u.id === editingUser.id
                        ? {
                            ...u,
                            name: userFormData.name,
                            email: userFormData.email,
                            role: userFormData.role,
                            planId: userFormData.planId,
                            status: userFormData.status,
                            planExpiresAt: userFormData.planId !== 'free' ? `${userFormData.planExpiresAt}T00:00:00` : undefined,
                        }
                        : u
                )
            );
        }

        setIsUserModalOpen(false);
    };

    const getStatusLabel = (status: User['status']) => {
        switch (status) {
            case 'active':
                return 'Ativo';
            case 'inactive':
                return 'Inativo';
            case 'suspended':
                return 'Suspenso';
            default:
                return status;
        }
    };

    const isExpiringSoon = (expiresAt?: string) => {
        if (!expiresAt) return false;
        const expiry = new Date(expiresAt);
        const now = new Date();
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        return expiry.getTime() - now.getTime() < thirtyDays && expiry > now;
    };

    const isExpired = (expiresAt?: string) => {
        if (!expiresAt) return false;
        return new Date(expiresAt) < new Date();
    };

    const getUserModalTitle = () => {
        switch (userModalMode) {
            case 'add':
                return 'Adicionar Novo Usuário';
            case 'edit':
                return `Editar Usuário: ${editingUser?.name}`;
            case 'changePlan':
                return `Alterar Plano: ${editingUser?.name}`;
            case 'renew':
                return `Renovar Plano: ${editingUser?.name}`;
            default:
                return 'Usuário';
        }
    };

    return (
        <div className={styles.layout}>
            <Sidebar />
            <main className={styles.main}>
                <Header />
                <div className={styles.content}>
                    <div className={styles.pageHeader}>
                        <div className={styles.headerLeft}>
                            <h2>Painel Administrativo</h2>
                            <p>Gerencie planos, usuários e configurações do sistema</p>
                        </div>
                        {activeTab === 'plans' && (
                            <Button onClick={handleNewPlan}>+ Novo Plano</Button>
                        )}
                        {activeTab === 'users' && (
                            <Button onClick={handleAddUser}>
                                <FiUserPlus size={16} style={{ marginRight: '0.5rem' }} />
                                Novo Usuário
                            </Button>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'plans' ? styles.active : ''}`}
                            onClick={() => setActiveTab('plans')}
                        >
                            <FiPackage size={16} />
                            Planos
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'users' ? styles.active : ''}`}
                            onClick={() => setActiveTab('users')}
                        >
                            <FiUsers size={16} />
                            Usuários
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'settings' ? styles.active : ''}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            <FiSettings size={16} />
                            Configurações
                        </button>
                    </div>

                    {/* Stats */}
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.users}`}>
                                <FiUsers size={20} />
                            </div>
                            <div className={styles.label}>Total de Usuários</div>
                            <div className={styles.value}>{totalUsers}</div>
                            <div className={styles.subLabel}>
                                <span className={styles.activeCount}>{activeUsers} ativos</span>
                                {inactiveUsers > 0 && <span className={styles.inactiveCount}> / {inactiveUsers} inativos</span>}
                            </div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.plans}`}>
                                <FiPackage size={20} />
                            </div>
                            <div className={styles.label}>Planos Ativos</div>
                            <div className={styles.value}>{totalPlans}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.revenue}`}>
                                <FiDollarSign size={20} />
                            </div>
                            <div className={styles.label}>Receita Mensal Est.</div>
                            <div className={styles.value}>{formatCurrency(estimatedRevenue)}</div>
                        </div>
                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.active}`}>
                                <FiCreditCard size={20} />
                            </div>
                            <div className={styles.label}>Assinaturas Ativas</div>
                            <div className={styles.value}>{activeSubscriptions}</div>
                        </div>
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'plans' && (
                        <div className={styles.plansGrid}>
                            {mockPlans.map(plan => (
                                <div key={plan.id} className={styles.planCard}>
                                    <div className={styles.planHeader}>
                                        <div className={styles.planInfo}>
                                            <h3>{plan.name}</h3>
                                            <p>{plan.description}</p>
                                        </div>
                                        <div className={styles.planPrice}>
                                            <div className={styles.amount}>
                                                {plan.price === 0 ? 'Grátis' : formatCurrency(plan.price)}
                                            </div>
                                            {plan.price > 0 && (
                                                <div className={styles.period}>/mês</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.planBody}>
                                        <div className={styles.planLimits}>
                                            <div className={styles.limitItem}>
                                                <div className={styles.label}>Negócios</div>
                                                <div className={styles.value}>{plan.maxAccounts}</div>
                                            </div>
                                            <div className={styles.limitItem}>
                                                <div className={styles.label}>Transações/mês</div>
                                                <div className={styles.value}>
                                                    {plan.maxTransactionsPerMonth === -1
                                                        ? 'Ilimitado'
                                                        : plan.maxTransactionsPerMonth}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.featuresSection}>
                                            <h4>Funcionalidades ({plan.features.length})</h4>
                                            <div className={styles.featuresList}>
                                                {plan.features.slice(0, 6).map(feature => (
                                                    <span key={feature} className={styles.featureTag}>
                                                        <FiCheck size={10} />
                                                        {ALL_FEATURES.find(f => f.key === feature)?.label || feature}
                                                    </span>
                                                ))}
                                                {plan.features.length > 6 && (
                                                    <span className={styles.featureTag}>
                                                        +{plan.features.length - 6} mais
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.planFooter}>
                                        <button
                                            className={`${styles.planBtn} ${styles.edit}`}
                                            onClick={() => handleEditPlan(plan)}
                                        >
                                            <FiEdit2 size={14} />
                                            Editar
                                        </button>
                                        <button
                                            className={`${styles.planBtn} ${styles.toggle} ${!plan.isActive ? styles.inactive : ''}`}
                                        >
                                            {plan.isActive ? (
                                                <>
                                                    <FiEye size={14} />
                                                    Ativo
                                                </>
                                            ) : (
                                                <>
                                                    <FiEyeOff size={14} />
                                                    Inativo
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className={styles.usersSection}>
                            <div className={styles.sectionHeader}>
                                <h3>Usuários Cadastrados</h3>
                                <div className={styles.searchInput}>
                                    <FiSearch size={16} className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        placeholder="Buscar usuários..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <table className={styles.usersTable}>
                                <thead>
                                    <tr>
                                        <th>Usuário</th>
                                        <th>Role</th>
                                        <th>Plano</th>
                                        <th>Status</th>
                                        <th>Expiração</th>
                                        <th>Cadastro</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id} className={user.status !== 'active' ? styles.inactiveRow : ''}>
                                            <td>
                                                <div className={styles.userInfo}>
                                                    <div className={styles.userAvatar}>
                                                        {user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className={styles.userDetails}>
                                                        <div className={styles.name}>{user.name}</div>
                                                        <div className={styles.email}>{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`${styles.roleBadge} ${styles[user.role]}`}>
                                                    {user.role === 'ADMIN' && <FiShield size={12} />}
                                                    {user.role === 'ADMIN' ? 'ADMIN' : 'Usuário'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={styles.planBadge}>
                                                    {getPlanName(user.planId)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${styles[user.status]}`}>
                                                    {getStatusLabel(user.status)}
                                                </span>
                                            </td>
                                            <td>
                                                {user.planExpiresAt ? (
                                                    <div className={styles.expiryCell}>
                                                        <span className={`${styles.expiryDate} ${isExpired(user.planExpiresAt) ? styles.expired : isExpiringSoon(user.planExpiresAt) ? styles.expiringSoon : ''}`}>
                                                            {formatDate(user.planExpiresAt)}
                                                        </span>
                                                        {isExpired(user.planExpiresAt) && (
                                                            <FiAlertCircle size={14} className={styles.expiredIcon} title="Expirado" />
                                                        )}
                                                        {isExpiringSoon(user.planExpiresAt) && !isExpired(user.planExpiresAt) && (
                                                            <FiCalendar size={14} className={styles.expiringSoonIcon} title="Expirando em breve" />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className={styles.noExpiry}>-</span>
                                                )}
                                            </td>
                                            <td>{user.createdAt ? formatDate(user.createdAt) : '-'}</td>
                                            <td>
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        className={styles.actionBtn}
                                                        title="Editar"
                                                        onClick={() => handleEditUser(user)}
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>
                                                    <button
                                                        className={styles.actionBtn}
                                                        title="Alterar Plano"
                                                        onClick={() => handleChangePlan(user)}
                                                    >
                                                        <FiPackage size={16} />
                                                    </button>
                                                    {user.planId && user.planId !== 'free' && (
                                                        <button
                                                            className={`${styles.actionBtn} ${styles.renewBtn}`}
                                                            title="Renovar Plano"
                                                            onClick={() => handleRenewPlan(user)}
                                                        >
                                                            <FiRefreshCw size={16} />
                                                        </button>
                                                    )}
                                                    {user.role !== 'ADMIN' && (
                                                        <>
                                                            <button
                                                                className={`${styles.actionBtn} ${user.status === 'active' ? styles.deactivateBtn : styles.activateBtn}`}
                                                                title={user.status === 'active' ? 'Inativar' : 'Ativar'}
                                                                onClick={() => handleToggleUserStatus(user)}
                                                            >
                                                                {user.status === 'active' ? <FiPause size={16} /> : <FiPlay size={16} />}
                                                            </button>
                                                            {user.status !== 'suspended' && (
                                                                <button
                                                                    className={`${styles.actionBtn} ${styles.suspendBtn}`}
                                                                    title="Suspender"
                                                                    onClick={() => handleSuspendUser(user)}
                                                                >
                                                                    <FiAlertCircle size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                className={`${styles.actionBtn} ${styles.deleteBtn}`}
                                                                title="Excluir"
                                                                onClick={() => handleDeleteUser(user)}
                                                            >
                                                                <FiTrash2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className={styles.usersSection}>
                            <div className={styles.sectionHeader}>
                                <h3>Configurações do Sistema</h3>
                            </div>
                            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                                <FiSettings size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p>Configurações do sistema em desenvolvimento.</p>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal de Edição de Plano */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingPlan ? `Editar Plano: ${editingPlan.name}` : 'Novo Plano'}
            >
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <Input
                                label="Nome do Plano"
                                value={formData.name}
                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Ex: Profissional"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <Input
                                label="Preço Mensal (R$)"
                                type="number"
                                value={formData.price}
                                onChange={e =>
                                    setFormData(prev => ({ ...prev, price: Number(e.target.value) }))
                                }
                                placeholder="0.00"
                            />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <Input
                                label="Descrição"
                                value={formData.description}
                                onChange={e =>
                                    setFormData(prev => ({ ...prev, description: e.target.value }))
                                }
                                placeholder="Descrição do plano..."
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <Input
                                label="Máximo de negócios (matriz + filiais)"
                                type="number"
                                min={1}
                                value={formData.maxAccounts}
                                onChange={e =>
                                    setFormData(prev => ({ ...prev, maxAccounts: Number(e.target.value) }))
                                }
                                placeholder="5"
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <Input
                                label="Transações por Mês"
                                type="number"
                                value={formData.maxTransactionsPerMonth}
                                min={0}
                                onChange={e =>
                                    setFormData(prev => ({
                                        ...prev,
                                        maxTransactionsPerMonth: Number(e.target.value),
                                    }))
                                }
                                placeholder="Ex: 100"
                            />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label>Funcionalidades Incluídas</label>
                            <div className={styles.featuresGrid}>
                                {ALL_FEATURES.map(feature => (
                                    <label key={feature.key} className={styles.featureCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={formData.features.includes(feature.key)}
                                            onChange={() => handleFeatureToggle(feature.key)}
                                        />
                                        <span>{feature.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit">
                            {editingPlan ? 'Salvar Alterações' : 'Criar Plano'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Modal de Usuário */}
            <Modal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                title={getUserModalTitle()}
            >
                <form onSubmit={handleUserSubmit}>
                    <div className={styles.formGrid}>
                        {(userModalMode === 'add' || userModalMode === 'edit') && (
                            <>
                                <div className={styles.formGroup}>
                                    <Input
                                        label="Nome Completo"
                                        value={userFormData.name}
                                        onChange={e => setUserFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Nome do usuário"
                                        required
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <Input
                                        label="E-mail"
                                        type="email"
                                        value={userFormData.email}
                                        onChange={e => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="email@exemplo.com"
                                        required
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.selectLabel}>Função</label>
                                    <SearchableSelect
                                        className={styles.select}
                                        value={userFormData.role}
                                        onChange={(v) => setUserFormData(prev => ({ ...prev, role: v as UserRole }))}
                                        options={[
                                            { value: 'user', label: 'Usuário' },
                                            { value: 'admin', label: 'Administrador' },
                                        ]}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.selectLabel}>Status</label>
                                    <SearchableSelect
                                        className={styles.select}
                                        value={userFormData.status}
                                        onChange={(v) => setUserFormData(prev => ({ ...prev, status: v as User['status'] }))}
                                        options={[
                                            { value: 'active', label: 'Ativo' },
                                            { value: 'inactive', label: 'Inativo' },
                                            { value: 'suspended', label: 'Suspenso' },
                                        ]}
                                    />
                                </div>
                            </>
                        )}

                        {(userModalMode === 'add' || userModalMode === 'edit' || userModalMode === 'changePlan') && (
                            <div className={styles.formGroup}>
                                <label className={styles.selectLabel}>Plano</label>
                                <SearchableSelect
                                    className={styles.select}
                                    value={userFormData.planId}
                                    onChange={(v) => setUserFormData(prev => ({ ...prev, planId: v }))}
                                    options={mockPlans.filter(p => p.isActive).map(plan => ({
                                        value: plan.id,
                                        label: `${plan.name}${plan.price > 0 ? ` - R$ ${plan.price}/mês` : ' (Grátis)'}`,
                                    }))}
                                />
                            </div>
                        )}

                        {(userModalMode === 'add' || userModalMode === 'edit' || userModalMode === 'changePlan' || userModalMode === 'renew') && userFormData.planId !== 'free' && (
                            <div className={styles.formGroup}>
                                <Input
                                    label={userModalMode === 'renew' ? 'Nova Data de Expiração' : 'Data de Expiração'}
                                    type="date"
                                    value={userFormData.planExpiresAt}
                                    onChange={e => setUserFormData(prev => ({ ...prev, planExpiresAt: e.target.value }))}
                                />
                            </div>
                        )}

                        {userModalMode === 'renew' && editingUser && (
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <div className={styles.renewInfo}>
                                    <FiRefreshCw size={20} />
                                    <div>
                                        <strong>Renovação de Plano</strong>
                                        <p>Plano atual: <strong>{getPlanName(editingUser.planId)}</strong></p>
                                        {editingUser.planExpiresAt && (
                                            <p>Expira em: <strong>{formatDate(editingUser.planExpiresAt)}</strong></p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {userModalMode === 'changePlan' && editingUser && (
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <div className={styles.changePlanInfo}>
                                    <FiPackage size={20} />
                                    <div>
                                        <strong>Alteração de Plano</strong>
                                        <p>Plano atual: <strong>{getPlanName(editingUser.planId)}</strong></p>
                                        <p>Novo plano: <strong>{getPlanName(userFormData.planId)}</strong></p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.modalFooter}>
                        <Button variant="outline" type="button" onClick={() => setIsUserModalOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit">
                            {userModalMode === 'add' && 'Criar Usuário'}
                            {userModalMode === 'edit' && 'Salvar Alterações'}
                            {userModalMode === 'changePlan' && 'Alterar Plano'}
                            {userModalMode === 'renew' && 'Renovar Plano'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};
